import { authenticatedRequestVerbose } from '../utils/auth';

const PRODUCT_TYPE = 'USDT-FUTURES';

/**
 * Bitget V2（经典合约账户 / mix）批量下单：POST /api/v2/mix/order/batch-place-order
 * 文档：https://www.bitget.com/api-doc/contract/trade/Batch-Order
 * 一次最多 20 笔，阶梯模型的几档限价单一次请求全部挂出去。
 *
 * force 默认 'post_only'：只做 maker——如果价格已经能立即成交（会吃单），交易所直接拒绝
 * 这一笔，不会意外变成 taker。
 *
 * ⚠️ one-way（单向）持仓模式下 side='sell' 即开空、side='buy' 即开多/平空。
 * 若账户是双向持仓模式（hedge-mode），Bitget 还要求每个订单额外传 tradeSide: 'open'|'close'，
 * 这里没有处理账户持仓模式探测，接真实资金前需要按账户实际设置补上。
 *
 * ⚠️ 响应里成功/失败订单分别在 data.successList / data.failureList 里按 clientOid 对应，
 * 这是按官方文档写的，还没能用真实 Key 验证过实际返回结构，换真实 Key 后建议核对一次。
 */
export const placeFutureBatchLimitOrders = async ({
  symbol,
  orders, // [{ side, price, size, clientOid, force?, presetStopLossPrice? }]
  marginMode = 'crossed',
  marginCoin = 'USDT',
  force = 'post_only',
  stopLossPrice,
}) => {
  const body = {
    symbol,
    productType: PRODUCT_TYPE,
    marginMode,
    marginCoin,
    orderList: orders.map(o => ({
      size: String(o.size),
      price: String(o.price),
      side: o.side,
      orderType: 'limit',
      force: o.force || force,
      ...(o.clientOid ? { clientOid: o.clientOid } : {}),
      // 开仓单直接带预设止损，成交后写入仓位止损，无需二次下单
      ...((o.presetStopLossPrice || stopLossPrice)
        ? { presetStopLossPrice: String(o.presetStopLossPrice || stopLossPrice) }
        : {}),
    })),
  };
  return authenticatedRequestVerbose('POST', '/api/v2/mix/order/batch-place-order', {}, body);
};

/** 单笔市价单：止损 / 止盈 / 结构失效离场用，只有一笔，不需要走批量接口 */
export const placeFutureMarketOrder = async ({
  symbol,
  side,
  size,
  marginMode = 'crossed',
  marginCoin = 'USDT',
  reduceOnly,
  clientOid,
}) => {
  const body = {
    symbol,
    productType: PRODUCT_TYPE,
    marginMode,
    marginCoin,
    size: String(size),
    side,
    orderType: 'market',
    ...(reduceOnly ? { reduceOnly: true } : {}),
    ...(clientOid ? { clientOid } : {}),
  };
  return authenticatedRequestVerbose('POST', '/api/v2/mix/order/place-order', {}, body);
};

/**
 * 仓位止损计划单：POST /api/v2/mix/order/place-tpsl-order
 * planType=pos_loss 时 size 可不传，触发后平掉该方向全部仓位。
 * 单向持仓空单 holdSide=sell；双向持仓空单 holdSide=short。
 */
export const placeFuturePosStopLoss = async ({
  symbol,
  triggerPrice,
  holdSide = 'sell',
  marginCoin = 'USDT',
  clientOid,
  triggerType = 'mark_price',
}) => {
  const body = {
    symbol,
    productType: PRODUCT_TYPE,
    marginCoin,
    planType: 'pos_loss',
    triggerPrice: String(triggerPrice),
    triggerType,
    holdSide,
    executePrice: '0',
    ...(clientOid ? { clientOid } : {}),
  };
  return authenticatedRequestVerbose('POST', '/api/v2/mix/order/place-tpsl-order', {}, body);
};

/**
 * 止盈 / 追踪类 TPSL：POST /api/v2/mix/order/place-tpsl-order
 * - profit_plan：定额止盈（需 size）
 * - pos_profit：仓位止盈（可平全部）
 * - moving_plan：移动止盈/追踪（需 callbackRatio，如 "12" 表示 12%）
 */
export const placeFutureTpslOrder = async ({
  symbol,
  planType,
  triggerPrice,
  holdSide = 'long',
  size,
  callbackRatio,
  marginCoin = 'USDT',
  clientOid,
  triggerType = 'mark_price',
  executePrice = '0',
}) => {
  const body = {
    symbol,
    productType: PRODUCT_TYPE,
    marginCoin,
    planType,
    triggerType,
    holdSide,
    executePrice: String(executePrice),
    ...(triggerPrice != null ? { triggerPrice: String(triggerPrice) } : {}),
    ...(size != null ? { size: String(size) } : {}),
    ...(callbackRatio != null ? { callbackRatio: String(callbackRatio) } : {}),
    ...(clientOid ? { clientOid } : {}),
  };
  return authenticatedRequestVerbose('POST', '/api/v2/mix/order/place-tpsl-order', {}, body);
};

/**
 * 普通计划委托：价格触及 triggerPrice 后按 orderType 成交。
 * POST /api/v2/mix/order/place-plan-order
 * planType=normal_plan + orderType=market → 触及后市价开仓（非 track_plan 追踪止损）。
 */
export const placeFuturePlanMarketOrder = async ({
  symbol,
  size,
  triggerPrice,
  side = 'buy',
  marginMode = 'crossed',
  marginCoin = 'USDT',
  triggerType = 'fill_price',
  tradeSide = 'open',
  clientOid,
}) => {
  const body = {
    planType: 'normal_plan',
    symbol,
    productType: PRODUCT_TYPE,
    marginMode,
    marginCoin,
    size: String(size),
    triggerPrice: String(triggerPrice),
    triggerType,
    side,
    tradeSide,
    orderType: 'market',
    ...(clientOid ? { clientOid } : {}),
  };
  return authenticatedRequestVerbose('POST', '/api/v2/mix/order/place-plan-order', {}, body);
};

/** 撤销普通委托：POST /api/v2/mix/order/cancel-order */
export const cancelFutureOrder = async ({ symbol, orderId, clientOid }) =>
  authenticatedRequestVerbose(
    'POST',
    '/api/v2/mix/order/cancel-order',
    {},
    {
      symbol,
      productType: PRODUCT_TYPE,
      ...(orderId ? { orderId: String(orderId) } : {}),
      ...(clientOid ? { clientOid } : {}),
    }
  );

/** 撤销计划委托：POST /api/v2/mix/order/cancel-plan-order */
export const cancelFuturePlanOrder = async ({
  symbol,
  orderId,
  clientOid,
  planType = 'normal_plan',
}) =>
  authenticatedRequestVerbose(
    'POST',
    '/api/v2/mix/order/cancel-plan-order',
    {},
    {
      symbol,
      productType: PRODUCT_TYPE,
      planType,
      ...(orderId ? { orderId: String(orderId) } : {}),
      ...(clientOid ? { clientOid } : {}),
    }
  );
